import { NextSeo } from 'next-seo';
import PropTypes from 'prop-types';

import AssembleLayout from '@/src/layouts/AssembleLayout';
import MainLayout from '@/src/layouts/MainLayout';
import { pageSolutionsDetailsByUIDGet } from '@/src/lib/prismic/queries';
import { urlHostResolver } from '@/src/lib/utils';
import SolutionsDetails from '@/src/views/solutions/SolutionsDetails';

export default function SolutionsDetailsPage({ doc, preview }) {
  const hero = doc?.data?.body?.find(slice => slice.slice_type === 'hero');
  const heroImage = hero?.primary?.image;
  const ogImage = {
    url: heroImage?.url,
    width: heroImage?.dimensions?.width,
    height: heroImage?.dimensions?.height,
    alt: heroImage?.alt,
  };

  return (
    <AssembleLayout Layout={MainLayout} Component={SolutionsDetails} doc={doc} preview={preview}>
      <NextSeo
        title={`${doc?.data?.page_title}`}
        description={doc?.data?.page_description}
        openGraph={{
          title: `${doc?.data?.page_title}`,
          url: urlHostResolver(`/solutions/${doc?.uid}`),
          images: [...(heroImage ? [ogImage] : [])], // TODO: Must find a more readable solution
        }}
      />
    </AssembleLayout>
  );
}

SolutionsDetailsPage.propTypes = {
  doc: PropTypes.object,
  preview: PropTypes.bool,
};

export async function getStaticPaths() {
  return {
    paths: [
      'k-12',
      'language-schools',
      'colleges',
      'educational-groups',
      'publishers',
      'after-school',
      'retail',
      'digital-platforms',
      'partners',
      'hub',
      'affiliates',
    ].map(item => ({
      params: { slug: item },
    })),
    fallback: false,
  };
}

export async function getStaticProps(context) {
  const { params, preview = false, previewData = {} } = context;
  const { ref } = previewData;
  const { slug: pageSolutionsDetailsUid } = params;

  const graphQuery = `{
    page-solutions-details {
      ...page-solutions-detailsFields
      body {
        ...on divider {
          non-repeat {
            ...non-repeatFields
          }
        }
        ...on hero {
          non-repeat {
            ...non-repeatFields
          }
        }
        ...on services {
          non-repeat {
            ...non-repeatFields
          }
          repeat {
            ...repeatFields
          }
        }
        ...on partners {
          non-repeat {
            ...non-repeatFields
          }
          repeat {
            ...repeatFields
          }
        }
        ...on contact {
          non-repeat {
            ...non-repeatFields
          }
        }
        ...on about {
          non-repeat {
            ...non-repeatFields
          }
          repeat {
            ...repeatFields
          }
        }
        ...on reviews {
          non-repeat {
            ...non-repeatFields
          }
          repeat {
            ...repeatFields
          }
        }
        ...on features {
          non-repeat {
            ...non-repeatFields
          }
          repeat {
            ...repeatFields
          }
        }
        ...on pricing {
          non-repeat {
            ...non-repeatFields
          }
          repeat {
            title
            subtitle
            pricing_group {
              ...on content-pricing-group {
                prices {
                  price {
                    ...on content-pricing {
                      ...content-pricingFields
                    }
                  }
                }
              }
            }
          }
        }
        ...on faq {
          non-repeat {
            ...non-repeatFields
          }
          repeat {
            ...repeatFields
          }
        }
      }
    }
  }`;

  const doc = await pageSolutionsDetailsByUIDGet({ pageSolutionsDetailsUid, graphQuery }, { ref });
  if (!doc) return { notFound: true };

  return {
    props: { doc, preview },
    revalidate: 3600,
  };
}
